import type { Signal, WritableSignal } from '@angular/core';
import { signal } from '@angular/core';

export interface FormTransaction<T> {
    readonly inTransaction: Signal<boolean>;
    beginTransaction(): void;
    commitTransaction(): void;
    rollbackTransaction(): void;
    transact(fn: () => void): void;
}

/**
 * Transactions are **re-entrant, with savepoints**: `beginTransaction` pushes a
 * frame (each with its own snapshot) instead of warning, `commitTransaction`
 * pops one, and `rollbackTransaction` pops one *and* restores that frame's
 * snapshot. `inTransaction` — which hosts watch to emit a single update / one
 * history entry — only flips back to false when the last frame closes.
 *
 * Nesting is the normal case, not an edge case:
 * - the widget's settings panel keeps a frame open for the whole editing session,
 *   so escape can revert it and the store sees one change when the panel closes;
 * - a sub-panel (save/cancel) opens its own frame on top, and cancelling it must
 *   revert only its own edits while leaving the session frame open — hence
 *   per-frame snapshots rather than one shared one;
 * - handlers that run during either (collection subscriptions replaying
 *   synchronously, `transact`-wrapped toggles) open and close short-lived frames.
 *
 * Every frame is balanced, so `transact`'s catch path rethrowing through
 * enclosing frames pops exactly the frames those callers opened. Calls with no
 * open frame at all still warn — they are genuinely unbalanced.
 */
export function createFormTransaction<T>(model: WritableSignal<T>): FormTransaction<T> {
    const inTransaction = signal(false);
    /** One snapshot per open frame; the last entry is the innermost savepoint. */
    const snapshots: T[] = [];

    return {
        inTransaction: inTransaction.asReadonly(),

        beginTransaction() {
            snapshots.push(structuredClone(model()));
            inTransaction.set(true);
        },

        commitTransaction() {
            if (!snapshots.length) {
                console.warn('No active transaction to commit');
                return;
            }

            snapshots.pop();

            if (!snapshots.length) {
                inTransaction.set(false);
            }
        },

        rollbackTransaction() {
            if (!snapshots.length) {
                console.warn('No active transaction to rollback');
                return;
            }

            model.set(snapshots.pop()!);

            if (!snapshots.length) {
                inTransaction.set(false);
            }
        },

        transact(fn: () => void) {
            this.beginTransaction();
            try {
                fn();
                this.commitTransaction();
            } catch (e) {
                this.rollbackTransaction();
                throw e;
            }
        },
    };
}

/**
 * Coalesces rapid writes (slider drags, colour pickers) into one transaction that
 * commits `delay` ms after the last call. Opens its own frame on the first call of
 * a window and closes exactly that frame — it never commits a transaction someone
 * else opened, so an enclosing panel transaction survives the throttled writes.
 */
export function transactThrottle(getTx: () => FormTransaction<any>, delay: number): (fn: () => void) => void {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let isWindowOpen = false;

    return (fn: () => void) => {
        const tx = getTx();

        if (!isWindowOpen) {
            tx.beginTransaction();
            isWindowOpen = true;
        }

        fn();

        if (timeoutId !== null) {
            clearTimeout(timeoutId);
        }

        timeoutId = setTimeout(() => {
            timeoutId = null;
            isWindowOpen = false;
            tx.commitTransaction();
        }, delay);
    };
}

export function transactMany(transactions: FormTransaction<any>[], fn: () => void): void {
    for (const tx of transactions) {
        tx.beginTransaction();
    }
    try {
        fn();
        for (const tx of transactions) {
            tx.commitTransaction();
        }
    } catch (e) {
        for (const tx of transactions) {
            tx.rollbackTransaction();
        }
        throw e;
    }
}
